import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, MessageSquareQuote, Clock, CheckCircle2, Archive, EyeOff, RefreshCw,
  Trash2, Save, User as UserIcon, Filter, AlertCircle,
} from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";

const STATUS_META = {
  new:      { label: "Nuovo",       cls: "bg-sheriff-gold/15 text-sheriff-gold border-sheriff-gold/40", Icon: Clock },
  reviewed: { label: "In revisione", cls: "bg-blue-500/15 text-blue-300 border-blue-500/40",           Icon: RefreshCw },
  resolved: { label: "Risolto",     cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",  Icon: CheckCircle2 },
  archived: { label: "Archiviato",  cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",           Icon: Archive },
};

const STATUS_OPTIONS = [
  { v: "all",      label: "Tutti" },
  { v: "new",      label: "Nuovi" },
  { v: "reviewed", label: "In revisione" },
  { v: "resolved", label: "Risolti" },
  { v: "archived", label: "Archiviati" },
];

const CAT_LABEL = {
  generale: "Generale",
  portale: "Portale",
  discord: "Discord",
  operativo: "Operativo",
  hr: "Risorse Umane",
  bug: "Bug",
  suggerimento: "Suggerimento",
};

export default function FeedbackManagerPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // { id, status, admin_note }
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    setLoading(true);
    const url = filter === "all" ? "/feedback" : `/feedback?status=${filter}`;
    api.get(url)
      .then(({ data }) => setItems(data || []))
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Errore caricamento feedback");
        setItems([]);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, reviewed: 0, resolved: 0, archived: 0 };
    items.forEach((f) => { c[f.status] = (c[f.status] || 0) + 1; });
    return c;
  }, [items]);

  const saveStatus = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/feedback/${editing.id}/status`, {
        status: editing.status,
        admin_note: editing.admin_note || null,
      });
      toast.success("Feedback aggiornato");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/feedback/${deleting.id}`);
      toast.success("Feedback eliminato");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div className="space-y-6" data-testid="feedback-manager-panel">
      {/* HEADER */}
      <div>
        <div className="uppercase-label text-sheriff-gold mb-2 flex items-center gap-2">
          <MessageSquareQuote className="h-4 w-4" /> Feedback Operatori — Comando
        </div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter text-[#F5E6B8] mb-2">
          Bacheca Feedback
        </h2>
        <p className="text-[#D4C480] max-w-2xl text-sm">
          Tutti i feedback inviati dagli operatori. I feedback anonimi non mostrano l&apos;autore.
          Assegna uno stato e rispondi con una nota interna visibile all&apos;operatore.
        </p>
      </div>

      {/* FILTERS + COUNTS */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2">
          <Filter className="h-4 w-4 text-sheriff-gold/70" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="feedback-filter-trigger" className="w-[200px] bg-[#143830] border-sheriff-gold/30 text-[#F5E6B8]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.v} value={s.v} data-testid={`feedback-filter-${s.v}`}>
                  {s.label} {s.v !== "all" && counts[s.v] !== undefined ? `(${counts[s.v]})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="feedback-manager-refresh"
          className="text-sheriff-gold hover:text-[#F5E6B8] inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-semibold transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </button>
        <div className="ml-auto text-[11px] font-mono text-sheriff-gold/70 uppercase tracking-widest">
          Totale: {items.length}
        </div>
      </div>

      {/* LIST */}
      {loading ? (
        <div className="rounded-xl bg-[#0F2620] border border-sheriff-gold/15 p-10 text-center text-[#F5E6B8]/50 text-sm">
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
          Caricamento feedback…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl bg-[#0F2620] border border-sheriff-gold/15 p-10 text-center text-[#F5E6B8]/50 text-sm" data-testid="feedback-manager-empty">
          Nessun feedback per questo filtro.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((f) => {
            const meta = STATUS_META[f.status] || STATUS_META.new;
            const StatusIcon = meta.Icon;
            return (
              <div
                key={f.id}
                data-testid={`feedback-mgr-item-${f.id}`}
                className="rounded-xl bg-[#0F2620] border border-sheriff-gold/20 hover:border-sheriff-gold/45 transition-colors p-4"
              >
                <div className="flex items-start gap-3">
                  <span className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest font-bold ${meta.cls}`}>
                    <StatusIcon className="h-3 w-3" /> {meta.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <h4 className="text-[#F5E6B8] font-semibold text-sm sm:text-base">{f.subject}</h4>
                      <span className="text-[10px] uppercase tracking-widest text-sheriff-gold/70 font-mono">
                        {CAT_LABEL[f.category] || f.category}
                      </span>
                    </div>
                    <div className="text-[11px] text-[#F5E6B8]/50 font-mono flex flex-wrap items-center gap-2 mt-1">
                      {f.anonymous ? (
                        <span className="inline-flex items-center gap-1 text-amber-300/80">
                          <EyeOff className="h-3 w-3" /> ANONIMO
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <UserIcon className="h-3 w-3" /> {f.author_name || "—"}
                          {f.author_role && <span className="opacity-60">({f.author_role})</span>}
                        </span>
                      )}
                      <span>·</span>
                      <span>{new Date(f.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    <p className="mt-3 text-sm text-[#F5E6B8]/85 whitespace-pre-wrap leading-relaxed">
                      {f.message}
                    </p>
                    {f.admin_note && (
                      <div className="mt-3 rounded-lg bg-sheriff-gold/8 border border-sheriff-gold/30 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-sheriff-gold font-bold mb-1">
                          Nota interna {f.reviewed_by && <span className="opacity-70">· {f.reviewed_by}</span>}
                        </div>
                        <p className="text-sm text-[#F5E6B8]/90 whitespace-pre-wrap">{f.admin_note}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-sheriff-gold/10">
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`feedback-delete-${f.id}`}
                    onClick={() => setDeleting(f)}
                    className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/10 h-8 px-3 text-xs"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina
                  </Button>
                  <Button
                    size="sm"
                    data-testid={`feedback-manage-${f.id}`}
                    onClick={() => setEditing({ id: f.id, status: f.status || "new", admin_note: f.admin_note || "" })}
                    className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold uppercase tracking-widest text-[11px] h-8 px-3"
                  >
                    <Save className="h-3.5 w-3.5 mr-1" /> Gestisci
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MANAGE DIALOG */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#0F2620] border border-sheriff-gold/30 text-[#F5E6B8]" data-testid="feedback-manage-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F5E6B8]">Gestisci Feedback</DialogTitle>
            <DialogDescription className="text-[#D4C480]/70">
              Aggiorna lo stato e opzionalmente rispondi con una nota interna visibile all&apos;autore.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-[#F5E6B8]/85 mb-1.5 block uppercase tracking-widest">Stato</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="feedback-status-trigger" className="bg-[#143830] border-sheriff-gold/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new" data-testid="fb-status-new">Nuovo</SelectItem>
                    <SelectItem value="reviewed" data-testid="fb-status-reviewed">In revisione</SelectItem>
                    <SelectItem value="resolved" data-testid="fb-status-resolved">Risolto</SelectItem>
                    <SelectItem value="archived" data-testid="fb-status-archived">Archiviato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#F5E6B8]/85 mb-1.5 block uppercase tracking-widest">Nota interna (opzionale)</Label>
                <Textarea
                  data-testid="feedback-admin-note"
                  value={editing.admin_note}
                  onChange={(e) => setEditing({ ...editing, admin_note: e.target.value })}
                  maxLength={1000}
                  rows={5}
                  placeholder="Risposta / commento del Comando…"
                  className="bg-[#143830] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-[#F5E6B8]/40 resize-y"
                />
                <div className="mt-1 text-[10px] text-right font-mono text-[#F5E6B8]/45">
                  {editing.admin_note.length} / 1000
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} data-testid="feedback-cancel-btn">Annulla</Button>
            <Button
              onClick={saveStatus}
              disabled={saving}
              data-testid="feedback-save-btn"
              className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold uppercase tracking-widest text-xs"
            >
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvo…</> : <><Save className="h-4 w-4 mr-2" /> Salva</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <DialogContent className="bg-[#0F2620] border border-rose-500/40 text-[#F5E6B8]" data-testid="feedback-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-200 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" /> Eliminare il feedback?
            </DialogTitle>
            <DialogDescription className="text-[#D4C480]/70">
              L&apos;azione è irreversibile. Il feedback sarà rimosso definitivamente dal sistema.
            </DialogDescription>
          </DialogHeader>
          {deleting && (
            <div className="rounded-lg bg-[#143830] border border-sheriff-gold/20 p-3">
              <div className="text-sm text-[#F5E6B8] font-medium">{deleting.subject}</div>
              <div className="text-[11px] text-[#F5E6B8]/50 mt-1 line-clamp-2">{deleting.message}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(null)} data-testid="feedback-delete-cancel">Annulla</Button>
            <Button
              onClick={deleteItem}
              data-testid="feedback-delete-confirm"
              className="bg-rose-500 hover:bg-rose-600 text-white font-bold uppercase tracking-widest text-xs"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
